import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { api, desde } from "../api.ts";
import { Cartao, SeloSeveridade, Vazio } from "../componentes/basicos.tsx";
import { ehAdmin, podeEscrever, useSessao } from "../sessao.tsx";
import { useDados } from "../usarDados.ts";
import { ROTULO_TIPO } from "./Painel.tsx";

type Resposta = {
  alertas: {
    id: number; tipo: string; severidade: string; titulo: string;
    empresa: string; hostname: string; aberto_em: string;
    email_enviado_em: string | null; resolvido_em: string | null;
    licenca_id: number | null; maquina_id: number | null;
    detalhe: Record<string, unknown>;
  }[];
  severidades: Record<string, number>;
};
type Destinatario = { id: number; email: string; tipos: string[]; ativo: boolean };

const SEVERIDADES: [string, string][] = [
  ["critica", "Criticos"], ["alta", "Altos"], ["media", "Medios"], ["baixa", "Baixos"],
];

export function Alertas() {
  const { usuario } = useSessao();
  const [params, setParams] = useSearchParams();
  const [aberto, setAberto] = useState<number | null>(null);
  const severidade = params.get("severidade") ?? "";
  const tipo = params.get("tipo") ?? "";
  const resolvidos = params.get("resolvidos") === "1";

  const q = new URLSearchParams();
  if (severidade) q.set("severidade", severidade);
  if (tipo) q.set("tipo", tipo);
  if (resolvidos) q.set("resolvidos", "1");

  const { dados: r, erro, carregando, recarregar } = useDados<Resposta>(`/alertas?${q}`);
  const admin = ehAdmin(usuario);
  const dest = useDados<Destinatario[]>(admin ? "/destinatarios" : "/sessao");
  const destinatarios = admin && Array.isArray(dest.dados) ? dest.dados : null;

  const trocar = (chave: string, valor: string) => {
    const p = new URLSearchParams(params);
    if (valor) p.set(chave, valor); else p.delete(chave);
    setParams(p);
  };

  return (
    <>
      <h1>Alertas</h1>

      {destinatarios && destinatarios.filter((d) => d.ativo).length === 0 && (
        <div className="alerta alerta--aviso">
          <span className="alerta__titulo">Nenhum destinatario ativo</span>
          Os alertas aparecem aqui, mas nao saem por e-mail. Cadastre um endereco abaixo.
        </div>
      )}

      <div className="tiles">
        {SEVERIDADES.map(([chave, rotulo]) => (
          <button key={chave} type="button"
                  className={`tile tile--clicavel${chave === "critica" && (r?.severidades[chave] ?? 0) > 0 ? " tile--alerta" : ""}`}
                  onClick={() => trocar("severidade", severidade === chave ? "" : chave)}>
            <div className="tile__valor">{r?.severidades[chave] ?? 0}</div>
            <div className="tile__rotulo">{rotulo}</div>
            <div className="tile__sub">{severidade === chave ? "filtrando" : "clique para filtrar"}</div>
          </button>
        ))}
      </div>

      <Cartao>
        <div className="chips" style={{ marginBottom: "var(--e-md)" }}>
          <button type="button" className={`chip${!tipo ? " chip--ativo" : ""}`}
                  onClick={() => trocar("tipo", "")}>Todos os tipos</button>
          {Object.entries(ROTULO_TIPO).map(([t, rotulo]) => (
            <button key={t} type="button" className={`chip${tipo === t ? " chip--ativo" : ""}`}
                    onClick={() => trocar("tipo", t)}>{rotulo}</button>
          ))}
          <button type="button" className={`chip${resolvidos ? " chip--ativo" : ""}`}
                  onClick={() => trocar("resolvidos", resolvidos ? "" : "1")}>
            incluir resolvidos
          </button>
        </div>

        {carregando && <div className="carregando">carregando...</div>}
        {erro && <div className="alerta alerta--erro">{erro}</div>}
        {r && r.alertas.length === 0 && (
          <Vazio titulo="Nada pendente">Nenhum alerta neste filtro.</Vazio>
        )}

        {r && r.alertas.length > 0 && (
          <div className="tabela-rolagem">
            <table className="tabela">
              <thead>
                <tr>
                  <th>Gravidade</th><th>Tipo</th><th>Alerta</th><th>Empresa</th>
                  <th>Maquina</th><th>Aberto</th><th>E-mail</th><th></th>
                </tr>
              </thead>
              <tbody>
                {r.alertas.map((a) => [
                  <tr key={a.id} onClick={() => setAberto(aberto === a.id ? null : a.id)}
                      style={{ cursor: "pointer" }}>
                    <td><SeloSeveridade severidade={a.severidade} /></td>
                    <td className="pequeno">{ROTULO_TIPO[a.tipo] ?? a.tipo}</td>
                    <td>{a.titulo}</td>
                    <td className="suave pequeno">
                      {a.licenca_id ? <Link to={`/licencas/${a.licenca_id}`}>{a.empresa}</Link> : a.empresa}
                    </td>
                    <td className="suave pequeno">
                      {a.maquina_id ? <Link to={`/maquinas/${a.maquina_id}`}>{a.hostname}</Link> : a.hostname}
                    </td>
                    <td className="suave pequeno">{desde(a.aberto_em)}</td>
                    <td className="pequeno">
                      {a.email_enviado_em
                        ? <span className="apagado">enviado {desde(a.email_enviado_em)}</span>
                        : <span className="selo selo--aviso">pendente</span>}
                    </td>
                    <td className="direita">
                      {a.resolvido_em ? <span className="selo selo--neutro">resolvido</span>
                        : podeEscrever(usuario) && (
                          <button className="botao botao--secundario botao--pequeno" type="button"
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    void api.post(`/alertas/${a.id}/resolver`).then(recarregar).catch(() => {});
                                  }}>Resolver</button>
                        )}
                    </td>
                  </tr>,
                  aberto === a.id && Object.keys(a.detalhe ?? {}).length > 0 ? (
                    <tr key={`${a.id}-d`}>
                      <td colSpan={8} style={{ background: "var(--superficie-alt)" }}>
                        <dl className="perfil">
                          {Object.entries(a.detalhe).map(([k, val]) => (
                            <div key={k} style={{ display: "contents" }}>
                              <dt>{k}</dt><dd className="mono pequeno">{String(val)}</dd>
                            </div>
                          ))}
                        </dl>
                      </td>
                    </tr>
                  ) : null,
                ])}
              </tbody>
            </table>
          </div>
        )}
      </Cartao>
    </>
  );
}
